'use strict';

const productQuantityStepper = require('../components/productQuantityStepper');
const productScarcity = require('../components/productScarcity');
const productPackSelect = require('../components/productPackSelect');
const addToCartToast = require('../utils/addToCartToast');
const notifyMe = require('../components/notifyMe');
const productImageResolver = require('../utils/productImageResolver');

/**
 * PLP product tiles: variation updates via Product-Variation JSON; scoped add-to-cart.
 */

/**
 * Reads the product id embedded in dwvar_<pid>_<attrId> keys (must match query pid).
 *
 * @param {URLSearchParams} searchParams - Parsed variation URL search params.
 * @returns {string} Product id or empty string.
 */
function pidFromDwvarParamKeys(searchParams) {
	let pid = '';
	searchParams.forEach(function (value, key) {
		if (pid || !key.startsWith('dwvar_')) {
			return;
		}
		const rest = key.substring('dwvar_'.length);
		const sep = rest.lastIndexOf('_');
		if (sep > 0) {
			pid = rest.substring(0, sep).replace(/__/g, '_');
		}
	});
	return pid;
}

/**
 * @param {jQuery} $shell - Product tile shell
 * @returns {number} Selected quantity (minimum 1)
 */
function getTileQuantity($shell) {
	const $qtyField = $shell.find('select.product-qty, input.product-qty').first();
	const quantity = parseInt($qtyField.val(), 10);

	if (isNaN(quantity) || quantity < 1) {
		return 1;
	}

	return quantity;
}

/**
 * PDP navigation links must stay vanity-safe (e.g. /p/...) without quantity
 * or broken query values (selectedProductUrl can emit quantity=undefined).
 * Tile qty is client-only (data-qty + stepper); Product-Variation keeps current qty
 * when color/pack changes; add-to-cart reads the select value.
 *
 * @param {string} url - Product or variation URL
 * @returns {string} Clean relative PDP URL
 */
function cleanProductNavigationUrl(url) {
	if (!url) {
		return url;
	}

	const parsed = new URL(url, window.location.href);
	parsed.searchParams.delete('quantity');

	const keysToDelete = [];
	parsed.searchParams.forEach(function (value, key) {
		if (value == null || value === '' || value === 'undefined' || value === 'null') {
			keysToDelete.push(key);
		}
	});
	keysToDelete.forEach(function (key) {
		parsed.searchParams.delete(key);
	});

	return parsed.pathname + parsed.search + parsed.hash;
}

/**
 * Keep attribute and jQuery data cache in sync (click handlers use .data('href')).
 *
 * @param {jQuery} $shell - Product tile shell
 * @param {string} productUrl - Clean PDP URL
 */
function setTileProductUrls($shell, productUrl) {
	$shell.attr('data-href', productUrl).data('href', productUrl);
	$shell.find('.js-update-variation-url').each(function () {
		const $el = $(this);
		$el.attr('data-href', productUrl).data('href', productUrl);
		if ($el.is('a')) {
			$el.attr('href', productUrl);
		}
	});
}

function toVariationUrl(variationUrl, $shell) {
	const sourceUrl = new URL(variationUrl, window.location.href);
	sourceUrl.pathname = sourceUrl.pathname
		.replace(/\/Product-Show(\/|$)/, '/Product-Variation$1')
		.replace(/\/Tile-Show(\/|$)/, '/Product-Variation$1');

	let hasDwvar = false;
	sourceUrl.searchParams.forEach(function (value, key) {
		// eslint-disable-line no-unused-vars
		if (key.startsWith('dwvar_')) {
			hasDwvar = true;
		}
	});

	if ($shell && $shell.length) {
		const urlPid = sourceUrl.searchParams.get('pid');
		let pid;
		if (hasDwvar) {
			// Tile data-pid is often a resolved variant (PLP factory); pack URLs use hit.productID in dwvar_* names.
			// Forcing data-pid breaks pid vs dwvar_<pid>_ consistency and yields 4xx/5xx on Product-Variation.
			pid = urlPid || pidFromDwvarParamKeys(sourceUrl.searchParams) || $shell.attr('data-pid');
		} else {
			pid = urlPid || $shell.attr('data-selected-pid') || $shell.attr('data-pid');
		}
		if (pid) {
			sourceUrl.searchParams.set('pid', pid);
		}

		sourceUrl.searchParams.set('quantity', String(getTileQuantity($shell)));
	}

	return sourceUrl.pathname + sourceUrl.search + sourceUrl.hash;
}

function updateTileSwatchValues(attr, $shell) {
	if ($shell.hasClass('product-tile--subscription-machine')) {
		return;
	}

	attr.values.forEach(function (attrValue) {
		const $swatch = $shell.find('[data-attr-value="' + attrValue.value + '"]');
		if (!$swatch.length) {
			return;
		}
		const $swatchBtn = $swatch.closest('.product-tile__swatch-btn');
		$swatch.toggleClass('selected', !!attrValue.selected);
		$swatchBtn.toggleClass('disabled-swatch', !!attrValue.selected);
		$swatch.removeClass('selectable unselectable').addClass(attrValue.selectable ? 'selectable' : 'unselectable');

		if (attrValue.url) {
			$swatchBtn.attr('data-url', attrValue.url);
		}
		// Keep existing data-url when Product-Variation omits url for non-orderable values.
		$swatchBtn.prop('disabled', false).removeAttr('disabled');
	});
}

function updateTileAttrs(attrs, $shell) {
	if (!attrs || !attrs.length) {
		return;
	}
	attrs.forEach(function (attr) {
		if (attr && attr.swatchable) {
			updateTileSwatchValues(attr, $shell);
			return;
		}
		if (attr) {
			productPackSelect.updatePackSelectValues(attr, $shell);
		}
	});
}

function updateTilePrice(product, $shell) {
	if (!product || !product.price || !product.price.html) {
		return;
	}
	$shell.find('.product-tile__price-row .price').replaceWith(product.price.html);
	const pricePerUnit = product.pricePerUnit || product.PricePerUnit;
	const displayValue = pricePerUnit && pricePerUnit.displayValue ? pricePerUnit.displayValue : '';
	$shell.find('[data-price-per-unit]').text(displayValue).toggleClass('d-none', !displayValue);
}

/**
 * @param {Object} product - Product Variation JSON product
 * @param {jQuery} $shell - Product tile shell
 * @param {string} [previousPid] - Selected pid before this variation response
 */
function updateTileProductName(product, $shell, previousPid) {
	if (!product) {
		return;
	}
	if (product.productName) {
		const $nameLink = $shell.find('.product-tile__name-link').first();
		if ($nameLink.length) {
			$nameLink.text(product.productName);
		}
	}

	// Quantity-only refresh must not replace the vanity PDP link with selectedProductUrl.
	if (previousPid && product.id && previousPid === product.id) {
		return;
	}

	const baseUrl = product.selectedProductUrl || $shell.attr('data-href') || $shell.attr('href');

	if (baseUrl) {
		setTileProductUrls($shell, cleanProductNavigationUrl(baseUrl));
	}
}

function updateTileProductDescription(product, $shell) {
	if (!product) {
		return;
	}
	if (product.shortDescription) {
		const $description = $shell.find('.product-tile__description').first();
		if ($description.length) {
			$description.text(product.shortDescription);
		}
	}
}

function updateLearnMoreModal(product, $shell) {
	const $modal = $shell.find('.product-tile__product-learn-more-panel');
	if (!$modal || !product) return;

	$modal.attr('id', 'lvz-product-learn-more-panel-' + product.id);
	$shell.find('.product-tile__product-learn-more-button').attr('aria-controls', 'lvz-product-learn-more-panel-' + product.id);

	if (product.productName) {
		const $nameLink = $modal.find('.product-tile__name-link').first();
		if ($nameLink.length) {
			$nameLink.text(product.productName);
		}
	}
	if (product.shortDescription) {
		const $description = $modal.find('.product-tile__description').first();
		if ($description.length) {
			$description.text(product.shortDescription);
		}
	}
}

function buildSrcset(mediumUrl, largeUrl) {
	if (!mediumUrl) {
		return '';
	}
	if (!largeUrl) {
		return mediumUrl + ' 375w';
	}
	return mediumUrl + ' 375w, ' + largeUrl + ' 1920w';
}

/**
 * Resets lazysizes state so data-src / data-srcset are applied again after AJAX variation updates.
 * Without this, the previous srcset (copied on unveil) keeps serving the old variation image.
 *
 * @param {jQuery} $img - Tile image element.
 */
function resetLazyImageState($img) {
	const lazySizes = window.lazySizes;
	const img = $img[0];

	$img.removeAttr('srcset sizes');

	if (lazySizes && lazySizes.cfg && img) {
		const cfg = lazySizes.cfg;
		lazySizes.rC(img, cfg.loadedClass);
		lazySizes.rC(img, cfg.loadingClass);
		lazySizes.rC(img, cfg.preloadClass);
		lazySizes.rC(img, cfg.fastLoadedClass);
		lazySizes.aC(img, cfg.lazyClass);
		return;
	}

	$img.removeClass('lazyloaded lazyloading lazypreload ls-is-cached').addClass('lazyload');
}

function updateImageElement($img, mediumImage, largeImage, fallbackAlt) {
	if (!$img.length || !mediumImage || !mediumImage.url) {
		return;
	}
	const mediumUrl = mediumImage.url;
	const largeUrl = largeImage && largeImage.url ? largeImage.url : mediumUrl;
	const title = mediumImage.title || '';
	const alt = fallbackAlt || $img.attr('alt') || '';

	resetLazyImageState($img);

	$img.attr({
		src: mediumUrl,
		'data-src': largeUrl,
		'data-srcset': buildSrcset(mediumUrl, largeUrl),
		'data-sizes': '100vw',
		alt: alt,
		title: title
	});

	if (window.lazySizes && window.lazySizes.loader) {
		window.lazySizes.loader.checkElems();
	}
}

function updateTileImages(product, $shell) {
	if (!product || !product.images) {
		return;
	}
	const mediumImages = product.images.medium || [];
	const largeImages = product.images.large || [];
	const resolvedImage = productImageResolver.resolveFromImageModel(product.images);
	if (!resolvedImage) {
		return;
	}
	updateImageElement($shell.find('.tile-image').first(), resolvedImage.image, resolvedImage.largeImage, product.productName);

	const $hoverImage = $shell.find('.tile-image-hover').first();
	if (!$hoverImage.length) {
		return;
	}

	const hoverImage = productImageResolver.getImageAt(mediumImages, 1) || productImageResolver.getImageAt(largeImages, 1);
	if (hoverImage) {
		const hoverLargeImage = productImageResolver.getImageAt(largeImages, 1) || hoverImage;
		updateImageElement($hoverImage, hoverImage, hoverLargeImage, product.productName);
		$hoverImage.removeClass('d-none');
	} else {
		// Keep markup stable; hide hover image when variation has no secondary image.
		$hoverImage.addClass('d-none');
	}
}

function updateTileAddToCart(product, $shell) {
	if (!product) {
		return;
	}
	const disabled = !product.readyToOrder || !product.available || !product.hasPrice;
	$shell.find('.product-tile__add-to-cart').prop('disabled', disabled).toggleClass('disabled', disabled);
	productQuantityStepper.syncAllQtySteppers($shell);
	notifyMe.updateProductState(product, $shell);
}

function applyVariationResponseToTile(data, $shell, variantContext) {
	if (!data || !data.product || !$shell || !$shell.length) {
		return;
	}
	const previousPid = $shell.attr('data-selected-pid');
	$shell.attr('data-selected-pid', data.product.id || previousPid);
	$shell.attr('data-ready-to-order', !!data.product.readyToOrder);
	const sales = data.product.price && data.product.price.sales;
	const mcpAttrs = {
		'data-sku': data.product.id || '',
		'data-price': sales ? String(sales.decimalPrice || sales.value || '') : '',
		'data-qty': String(getTileQuantity($shell))
	};
	$shell.attr(mcpAttrs);
	$shell.find('[data-mcp-atc="true"], .product-tile__add-to-cart, article.product-tile').attr(mcpAttrs);
	updateTileAttrs(data.product.variationAttributes, $shell);
	productQuantityStepper.updateQuantities(data.product, $shell);
	updateTileProductName(data.product, $shell, previousPid);
	updateTileProductDescription(data.product, $shell);
	updateTileImages(data.product, $shell);
	updateTilePrice(data.product, $shell);
	updateTileAddToCart(data.product, $shell);
	productQuantityStepper.updateQuantities(data.product, $shell);
	productScarcity.updateScarcity(data.product, $shell);
	if (window.lavazzaGtm && window.lavazzaGtm.ecommerce) {
		window.lavazzaGtm.ecommerce.syncTileTealiumProduct($shell, data.product);
	}
	if (variantContext && window.lavazzaGtm && window.lavazzaGtm.plp) {
		window.lavazzaGtm.plp.trackVariantSelected(data.product, variantContext.name, variantContext.value);
	}
	syncTileChrome($shell);
	updateLearnMoreModal(data.product, $shell);
}

/**
 * @param {jQuery} $shell - Product tile shell
 * @param {string} variationUrl
 * @param {Object} [variantContext] - When the refresh is triggered by a variant pick, the
 * attribute name/value being tracked (label/category can only be read from the response).
 */
function refreshTileFromVariationUrl($shell, variationUrl, variantContext) {
	const requestUrl = toVariationUrl(variationUrl, $shell);
	$.spinner().start();
	$.ajax({
		lvzSkipAutoSpinner: true,
		url: requestUrl,
		method: 'GET',
		success: function (data) {
			$shell.attr('data-last-variation-url', requestUrl);
			applyVariationResponseToTile(data, $shell, variantContext);
			$.spinner().stop();
		},
		error: function () {
			$.spinner().stop();
		}
	});
}

function syncTileChrome($root) {
	productQuantityStepper.syncAllQtySteppers($root);
	productPackSelect.syncPackSelectUI($root);
}

module.exports = function initProductTilePLP() {
	if (window.__lvzProductTileCartBound) {
		return;
	}

	window.__lvzProductTileCartBound = true;

	syncTileChrome();

	$('body').on('search:updateDom', function () {
		syncTileChrome();
	});

	$(document).on('click', '.product-tile-shell button', function (e) {
		if ($(e.target).closest('[data-notify-me-trigger], [data-notify-me-modal]').length) {
			return;
		}

		// console.log('[productTileCart] tile shell button click', {
		// 	target: e.target,
		// 	currentTarget: e.currentTarget,
		// 	buttonClass: this.className,
		// });
		if ($(this).is('.product-tile__add-to-cart')) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();
	});

	$(document).on('click', '.product-tile__name-link, .product-tile__global-link', function (e) {
		const $link = $(this);
		const url = $link.data('href') || $link.attr('href');

		if (!url) {
			return;
		}

		const $shell = $link.closest('.product-tile-shell');
		if (window.lavazzaGtm && window.lavazzaGtm.ecommerce) {
			window.lavazzaGtm.ecommerce.trackProductTileClick($shell);
		}

		// Cmd/Ctrl/Shift/middle-click must keep the browser's native new-tab behavior.
		if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button === 1) {
			return;
		}

		e.preventDefault();
		$.spinner().start();
		window.location.href = url;
	});

	$(document).on('click', '.product-tile__listing-btn, .product-tile__swatches-more', function () {
		const url = $(this).data('href') || $(this).attr('href');
		if (url) {
			$.spinner().start();
			window.location.href = url;
		}
	});

	$(document).on('change', '.product-tile-shell .product__variation-select', function () {
		const url = $(this).val();
		if (!url) {
			return;
		}
		const $selectedOption = $(this).find('option:selected');
		const variantContext = {
			name: 'packsize',
			value: $.trim($selectedOption.text())
		};
		refreshTileFromVariationUrl($(this).closest('.product-tile-shell'), url, variantContext);
	});

	$(document).on('click', '.product-tile-shell .product-tile__swatch-btn', function (e) {
		const $shell = $(this).closest('.product-tile-shell');
		const $btn = $(this);

		if ($shell.hasClass('product-tile--subscription-machine')) {
			return;
		}

		e.preventDefault();

		// SFRA sets urlUnselectVariationValue on the selected selectable swatch.
		// Re-clicking would drop the color and flip Notify me ↔ qty on OOS variants.
		if ($btn.find('.swatch-value.selected').length || $btn.hasClass('disabled-swatch')) {
			return;
		}

		const url = $btn.attr('data-url');
		if (!url) {
			return;
		}
		$shell.find('.product-tile__swatch-btn').removeClass('disabled-swatch');
		$btn.addClass('disabled-swatch');
		const variantContext = {
			name: 'color',
			value: $btn.find('.color-value').attr('data-attr-value')
		};
		refreshTileFromVariationUrl($shell, url, variantContext);
	});

	// Qty +/- must not call Product-Variation: productQuantityStepper syncs data-qty
	// on the shell / article / ATC. Variation refresh stays on color/pack only.

	$(document).on('click', '.product-tile-shell .product-tile__add-to-cart', function () {
		const $shell = $(this).closest('.product-tile-shell');
		const addToCartUrl =
			$(this).closest('.col-plp').find('.add-to-cart-url').val() ||
			$shell.find('.add-to-cart-url').first().val() ||
			$shell
				.closest('.lvz-generic-product-carousel__item, .lvz-collection-layer__product-item')
				.find('.add-to-cart-url')
				.first()
				.val();
		const pid = $shell.attr('data-selected-pid');
		const $qtyField = $shell.find('select.product-qty, input.product-qty').first();
		const qty = parseInt($qtyField.val(), 10) || 1;

		// console.log('[productTileCart] add to cart click', {
		// 	shell: $shell.get(0),
		// 	addToCartUrl: addToCartUrl,
		// 	pid: pid,
		// 	qty: qty,
		// });

		if (!addToCartUrl || !pid) {
			// console.log('[productTileCart] add to cart aborted', {
			// 	reason: !addToCartUrl ? 'missing addToCartUrl' : 'missing pid',
			// 	addToCartUrl: addToCartUrl,
			// 	pid: pid,
			// });
			return;
		}

		$('body').trigger('product:beforeAddToCart', this);

		const childProducts = $(this).attr('data-child-products');
		const requestData = {
			pid: pid,
			quantity: qty,
			options: JSON.stringify([])
		};

		if (childProducts) {
			requestData.childProducts = childProducts;
		}

		$.ajax({
			lvzSkipAutoSpinner: true,
			url: addToCartUrl,
			method: 'POST',
			data: requestData,
			success: function (data) {
				// console.log('[productTileCart] add to cart success', data);
				$('.minicart').trigger('count:update', data);
				addToCartToast.show({
					message: data.error ? data.message : undefined,
					quantityTotal: data.quantityTotal,
					error: data.error
				});
				$('body').trigger('product:afterAddToCart', [data, $shell]);
				// The shared SFRA beforeAddToCart event starts the PDP spinner even
				// for tiles. This flow owns the matching stop after its request ends.
				$.spinner().stop();
			},
			error: function (xhr, status, error) {
				// console.log('[productTileCart] add to cart error', {
				// 	xhr: xhr,
				// 	status: status,
				// 	error: error,
				// });
				$.spinner().stop();
			}
		});
	});
};
